/**
 * Urgency level to status style mapping
 * Maps urgency levels to appropriate status styles
 */

export const DATA_BADGE_STYLES: Record<'DEFAULT' | 'DANGER', string> = {
  DEFAULT: 'tw:bg-white tw:text-zinc-800 tw:border-zinc-300',
  DANGER: 'tw:bg-white tw:text-red-700 tw:border-zinc-300',
};

export const URGENCY_LEVEL_STYLE_MAP: Record<string, string> = {
  1: DATA_BADGE_STYLES.DEFAULT,
  2: DATA_BADGE_STYLES.DEFAULT,
  3: DATA_BADGE_STYLES.DEFAULT,
  4: DATA_BADGE_STYLES.DEFAULT,
  5: DATA_BADGE_STYLES.DANGER,
};

/**
 * Urgency level icon mapping
 */
export const URGENCY_LEVEL_ICONS: Record<string, string> = {
  1: 'far fa-circle-info tw:text-zinc-600',
  2: 'fas fa-exclamation-triangle tw:text-stoplight-warning',
  3: 'fas fa-exclamation-triangle tw:text-stoplight-warning',
  4: 'fas fa-bell tw:text-red-700',
  5: 'fas fa-bell-on tw:text-red-700',
};

export const SOURCE_TYPE_ORGANIZATION_BADGE_STYLE: string =
  'tw:text-xs tw:text-zinc-600 tw:bg-zinc-300 tw:border tw:border-zinc-400 tw:rounded-sm tw:px-1 tw:py-0.5';

/**
 * Patient Compensation Fund (PCF) flag for a connection.
 * - enrolled: the PCF_ENROLLED event has been logged
 * - required: awaiting enrollment in a state where participation is mandatory
 * - eligible: awaiting enrollment in a state where participation is voluntary
 * - null: no flag (no PCF requirement on the connection)
 */
export type PcfFlag = 'eligible' | 'required' | 'enrolled';

export const PCF_FLAG_BADGES: Record<
  PcfFlag,
  { label: string; color: 'blue' | 'amber' | 'green' }
> = {
  eligible: { label: 'PCF Eligible', color: 'blue' },
  required: { label: 'PCF Required', color: 'amber' },
  enrolled: { label: 'PCF Enrolled', color: 'green' },
};

export function getPcfFlag(connection: {
  requires_pcf_enrollment?: boolean;
  pcf_state_is_mandatory?: boolean | null;
  pcf_enrolled_at?: string | null;
}): PcfFlag | null {
  if (connection.pcf_enrolled_at) return 'enrolled';
  if (!connection.requires_pcf_enrollment) return null;
  return connection.pcf_state_is_mandatory ? 'required' : 'eligible';
}

/**
 * Two-state PCF flag for list/card views (staff): connections that require
 * enrollment are either PCF Required (awaiting) or PCF Enrolled — the
 * mandatory/voluntary distinction stays on the drawer.
 */
export function getPcfStatusFlag(connection: {
  requires_pcf_enrollment?: boolean;
  pcf_enrolled_at?: string | null;
}): Extract<PcfFlag, 'required' | 'enrolled'> | null {
  if (!connection.requires_pcf_enrollment) return null;
  return connection.pcf_enrolled_at ? 'enrolled' : 'required';
}
